<script lang="ts">
  import { slide } from "svelte/transition";
  import { api } from "../lib/api/index.js";
  import { greeting, plural } from "../lib/format.js";
  import { router } from "../lib/router.svelte.js";
  import { library, type Piece } from "../lib/stores/library.svelte.js";
  import { live } from "../lib/stores/live.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { session } from "../lib/stores/session.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Cover from "../components/Cover.svelte";
  import Icon from "../components/Icon.svelte";
  import PieceCard from "../components/PieceCard.svelte";
  import PieceRow from "../components/PieceRow.svelte";

  const chips = $derived([
    { id: "all", label: "Alle", count: library.pieces.length, color: "" },
    { id: "fav", label: "Favoriten", count: library.favorites.length, color: "" },
    { id: "recent", label: "Zuletzt gespielt", count: library.recent.length, color: "" },
    ...(library.sharedWithMe.length
      ? [{ id: "shared", label: "Mit mir geteilt", count: library.sharedWithMe.length, color: "" }]
      : []),
    ...library.categories.map((c) => ({
      id: `cat:${c.id}`,
      label: c.name,
      count: library.pieces.filter((p) => p.categoryIds.includes(c.id)).length,
      color: c.color,
    })),
  ]);

  const tiles = $derived(
    library.categories
      .map((category) => {
        const items = library.pieces.filter((p) => p.categoryIds.includes(category.id));
        return { category, count: items.length, cover: items[0] ?? null };
      })
      .filter((tile) => tile.count > 0),
  );

  const filterLabel = $derived(chips.find((c) => c.id === library.filter)?.label ?? "");
  const firstName = $derived(session.user?.displayName ?? "");

  // „Zuletzt gespielt“: standardmäßig zu; der Zustand gilt pro Nutzer auf allen Geräten.
  const recentOpen = $derived(live.settings.library.quickAccess.recentOpen);
  async function toggleRecent(): Promise<void> {
    const before = live.settings;
    const next = !recentOpen;
    live.settings = { ...before, library: { ...before.library, quickAccess: { recentOpen: next } } };
    try {
      live.settings = await api.patchSettings({ library: { quickAccess: { recentOpen: next } } });
    } catch (error) {
      live.settings = before;
      ui.error(error);
    }
  }

  function mosaic(ids: string[]): Piece[] {
    return ids
      .map((id) => library.byId.get(id))
      .filter((p): p is Piece => Boolean(p))
      .slice(0, 4);
  }
</script>

<div class="library" class:narrow={!ui.wide}>
  <header class="head">
    <h1>{greeting()}{firstName ? `, ${firstName}` : ""}</h1>
    <div class="head-actions">
      <label class="search">
        <Icon name="search" size={20} stroke={2.4} />
        <span class="sr-only">Noten suchen</span>
        <input
          type="search"
          placeholder="Was wollt ihr spielen?"
          autocomplete="off"
          enterkeyhint="search"
          bind:value={library.query}
        />
        {#if library.query}
          <button type="button" class="clear" aria-label="Suche leeren" onclick={() => (library.query = "")}>
            <Icon name="close" size={16} stroke={2.6} />
          </button>
        {/if}
      </label>
      {#if ui.wide}
        <button type="button" class="btn btn-ghost add-btn" onclick={() => (ui.uploadOpen = true)}>
          <Icon name="plus" size={18} stroke={2.4} /> Hinzufügen
        </button>
      {:else}
        <button
          type="button"
          class="icon-btn"
          aria-label="Noten hinzufügen"
          onclick={() => (ui.uploadOpen = true)}
        >
          <Icon name="upload" />
        </button>
      {/if}
    </div>
  </header>

  <div class="chips hscroll" role="toolbar" aria-label="Filter">
    {#each chips as chip (chip.id)}
      <button
        type="button"
        class="chip"
        aria-pressed={library.filter === chip.id}
        onclick={() => library.setFilter(chip.id)}
      >
        {#if chip.id === "fav"}<span class="star"><Icon name="starFill" size={14} /></span>{/if}
        {#if chip.color}<span class="dot" style:background={chip.color}></span>{/if}
        {chip.label}
        <span class="count">{chip.count}</span>
      </button>
    {/each}
  </div>

  {#if !library.loaded}
    <div class="skeleton-grid" aria-label="Lädt">
      {#each Array.from({ length: 10 }) as _, i (i)}<div class="skeleton"></div>{/each}
    </div>
  {:else if library.searching}
    <section class="section">
      <h2>
        {library.filter !== "all" ? `${filterLabel} · ` : ""}{plural(
          library.results.length,
          "Stück",
          "Stücke",
        )}
      </h2>
      {#if library.results.length === 0}
        <div class="empty">
          <h3>Nichts gefunden</h3>
          <p class="t2">Versuch es mit einem anderen Suchbegriff oder Filter.</p>
        </div>
      {:else if ui.wide}
        <div class="grid">
          {#each library.results as piece, i (piece.id)}<PieceCard
              {piece}
              delay={Math.min(i, 20) * 25}
            />{/each}
        </div>
      {:else}
        <div class="rows">
          {#each library.results as piece, i (piece.id)}<PieceRow
              {piece}
              delay={Math.min(i, 20) * 25}
            />{/each}
        </div>
      {/if}
    </section>
  {:else if library.pieces.length === 0}
    <div class="empty big">
      <h3>Noch keine Noten</h3>
      <p class="t2">Lade eure erste PDF hoch – oder legt Dateien direkt in den Ordner „sheets“.</p>
      <button type="button" class="btn btn-primary" onclick={() => (ui.uploadOpen = true)}>
        <Icon name="upload" size={18} /> PDF hochladen
      </button>
    </div>
  {:else}
    {#if library.favorites.length}
      <section class="section">
        <div class="section-head">
          <h2>Eure Favoriten</h2>
          <button type="button" class="more-link" onclick={() => library.setFilter("fav")}
            >Alle anzeigen</button
          >
        </div>
        <div class="row-scroll hscroll">
          {#each library.favorites.slice(0, 12) as piece (piece.id)}
            <a
              class="tile cover-zoom"
              href="./noten/{piece.id}"
              onclick={(e) => (e.preventDefault(), router.go(`/noten/${piece.id}`))}
            >
              <Cover src={piece.thumb} />
              <span class="tile-title ellipsis">{piece.name}</span>
              <span class="tile-sub t3 ellipsis">{piece.artist}</span>
            </a>
          {/each}
        </div>
      </section>
    {/if}

    {#if library.recent.length}
      <section class="section">
        <div class="section-head">
          <h2>
            <button
              type="button"
              class="fold"
              aria-expanded={recentOpen}
              aria-controls="recent-row"
              onclick={toggleRecent}
              >Zuletzt gespielt<span class="fold-count t3">{library.recent.length}</span><span
                class="chevron"
                class:open={recentOpen}><Icon name="chevronDown" size={20} stroke={2.4} /></span
              ></button
            >
          </h2>
          {#if recentOpen}
            <button type="button" class="more-link" onclick={() => library.setFilter("recent")}
              >Alle anzeigen</button
            >
          {/if}
        </div>
        {#if recentOpen}
          <div id="recent-row" class="row-scroll hscroll" transition:slide={{ duration: 320 }}>
            {#each library.recent as piece (piece.id)}
              <a
                class="tile cover-zoom"
                href="./noten/{piece.id}"
                onclick={(e) => (e.preventDefault(), router.go(`/noten/${piece.id}`))}
              >
                <Cover src={piece.thumb} />
                <span class="tile-title ellipsis">{piece.name}</span>
                <span class="tile-sub t3 ellipsis">{piece.artist}</span>
              </a>
            {/each}
          </div>
        {/if}
      </section>
    {/if}

    {#if tiles.length}
      <section class="section">
        <h2>Stöbern</h2>
        <div class="cats">
          {#each tiles as tile (tile.category.id)}
            <button
              type="button"
              class="cat"
              style:--c={tile.category.color}
              onclick={() => library.setFilter(`cat:${tile.category.id}`)}
            >
              <span class="cat-text">
                <span class="cat-name">{tile.category.icon} {tile.category.name}</span>
                <span class="cat-count">{plural(tile.count, "Stück", "Stücke")}</span>
              </span>
              {#if tile.cover}<img src={tile.cover.thumb} alt="" loading="lazy" />{/if}
            </button>
          {/each}
        </div>
      </section>
    {/if}

    {#if playlists.list.length}
      <section class="section">
        <h2>Deine Setlists</h2>
        <div class="setlists">
          {#each playlists.list as playlist (playlist.id)}
            <button type="button" class="setlist" onclick={() => router.go(`/setlists/${playlist.id}`)}>
              <span class="mosaic" style:background={playlist.accentColor}>
                {#each mosaic(playlist.items) as piece (piece.id)}<img
                    src={piece.thumb}
                    alt=""
                    loading="lazy"
                  />{/each}
                {#if playlist.items.length === 0}<span class="mosaic-icon">{playlist.icon}</span>{/if}
              </span>
              <span class="setlist-name ellipsis">{playlist.name}</span>
              <span class="t3 setlist-meta"
                >{plural(playlist.items.length, "Stück", "Stücke")}{playlists.activeId === playlist.id
                  ? " · aktuell"
                  : ""}</span
              >
            </button>
          {/each}
        </div>
      </section>
    {/if}

    <section class="section">
      <div class="section-head">
        <h2>Alle Stücke</h2>
        <span class="t3 small">{plural(library.pieces.length, "Stück", "Stücke")} · Titel A–Z</span>
      </div>
      {#if ui.wide}
        <div class="grid">
          {#each library.results as piece, i (piece.id)}<PieceCard
              {piece}
              delay={Math.min(i, 20) * 25}
            />{/each}
        </div>
      {:else}
        <div class="rows">
          {#each library.results as piece, i (piece.id)}<PieceRow
              {piece}
              delay={Math.min(i, 12) * 25}
            />{/each}
        </div>
      {/if}
    </section>
  {/if}
</div>

<style>
  .library {
    padding: calc(30px + var(--safe-top)) 32px 40px;
    display: flex;
    flex-direction: column;
    gap: 22px;
  }
  .narrow {
    padding: calc(18px + var(--safe-top)) 18px 24px;
    gap: 16px;
  }
  .head {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
  }
  h1 {
    font-size: 38px;
    flex: 1 1 260px;
  }
  .narrow h1 {
    font-size: 28px;
  }
  .head-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex: 1 1 360px;
    justify-content: flex-end;
  }
  .narrow .head-actions {
    flex-basis: 100%;
  }
  .search {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 48px;
    padding: 0 14px 0 16px;
    border-radius: var(--r-pill);
    background: var(--text);
    color: var(--text-on-light);
    flex: 1;
    max-width: 360px;
    transition: box-shadow var(--dur-fast);
  }
  .narrow .search {
    max-width: none;
    border-radius: var(--r-md);
  }
  .search:focus-within {
    box-shadow: 0 0 0 3px var(--accent);
  }
  .search input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 15px;
    font-weight: 700;
    color: var(--text-on-light);
  }
  .search input::-webkit-search-cancel-button {
    display: none;
  }
  .clear {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.08);
  }
  .add-btn {
    height: 48px;
  }
  .chips {
    display: flex;
    gap: 8px;
    margin: -6px -32px 0;
    padding: 0 32px;
  }
  .narrow .chips {
    margin: 0 -18px;
    padding: 0 18px;
  }
  .count {
    font-size: 12px;
    opacity: 0.6;
  }
  .star {
    color: var(--accent-2);
    display: inline-flex;
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .section {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .section h2 {
    font-size: 22px;
  }
  .narrow .section h2 {
    font-size: 20px;
  }
  .fold {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font: inherit;
    color: inherit;
  }
  .fold-count {
    font-size: 14px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .chevron {
    display: inline-flex;
    color: var(--text-2);
    transform: rotate(-90deg);
    transition: transform var(--dur-base) var(--ease-spring);
  }
  .chevron.open {
    transform: none;
  }
  .fold:hover .chevron {
    color: var(--text);
  }
  .section-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }
  .more-link {
    color: var(--text-2);
    font-weight: 700;
    font-size: 13px;
  }
  .small {
    font-size: 13px;
    font-weight: 700;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(156px, 1fr));
    gap: 6px 4px;
    margin: 0 -10px;
  }
  .grid > :global(*) {
    content-visibility: auto;
    contain-intrinsic-size: 240px;
  }
  .rows {
    display: flex;
    flex-direction: column;
  }
  .row-scroll {
    display: flex;
    gap: 18px;
    margin: 0 -32px;
    padding: 0 32px 4px;
  }
  .narrow .row-scroll {
    gap: 14px;
    margin: 0 -18px;
    padding: 0 18px 4px;
  }
  .tile {
    width: 150px;
    flex: 0 0 150px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .narrow .tile {
    width: 122px;
    flex-basis: 122px;
  }
  .tile-title {
    font-weight: 700;
    font-size: 14px;
  }
  .tile-sub {
    font-size: 12px;
    font-weight: 600;
    margin-top: -4px;
  }
  .cats {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 14px;
  }
  .narrow .cats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .cat {
    position: relative;
    height: 118px;
    border-radius: var(--r-lg);
    overflow: hidden;
    display: flex;
    align-items: flex-end;
    padding: 14px;
    text-align: left;
    background: color-mix(in oklab, var(--c) 82%, black);
    transition: transform var(--dur-base) var(--ease-spring);
  }
  .narrow .cat {
    height: 96px;
    padding: 12px;
  }
  .cat:hover {
    transform: translateY(-4px) rotate(-0.5deg);
  }
  .cat:active {
    transform: scale(0.96);
  }
  .cat-text {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: #fff;
  }
  .cat-name {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 18px;
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.25);
  }
  .narrow .cat-name {
    font-size: 16px;
  }
  .cat-count {
    font-size: 12px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.88);
  }
  .cat img {
    position: absolute;
    right: -12px;
    bottom: -24px;
    width: 74px;
    height: 100px;
    object-fit: cover;
    object-position: top;
    border-radius: 4px;
    transform: rotate(20deg);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.45);
    transition: transform var(--dur-sheet) var(--ease-spring);
    background: var(--paper);
  }
  .cat:hover img {
    transform: rotate(12deg) translateY(-6px);
  }
  .setlists {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }
  .narrow .setlists {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .setlist {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 12px;
    border-radius: var(--r-lg);
    background: var(--surface-2);
    text-align: left;
    transition:
      background var(--dur-base),
      transform var(--dur-base) var(--ease-spring);
  }
  .setlist:hover {
    background: #1f1f25;
    transform: translateY(-3px);
  }
  .mosaic {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
    aspect-ratio: 1;
    border-radius: 10px;
    overflow: hidden;
  }
  .mosaic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
    background: var(--paper);
  }
  .mosaic-icon {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
  }
  .setlist-name {
    font-weight: 800;
    font-size: 15px;
  }
  .setlist-meta {
    font-size: 12px;
    font-weight: 600;
    margin-top: -4px;
  }
  .empty {
    padding: 50px 0;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
  }
  .empty.big {
    padding: 80px 0;
    gap: 12px;
  }
  .empty h3 {
    font-size: 22px;
  }
  .empty p {
    margin: 0;
  }
  .skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(156px, 1fr));
    gap: 20px;
  }
  .skeleton {
    aspect-ratio: 4 / 5.6;
    border-radius: 12px;
    background: linear-gradient(100deg, var(--surface-2) 30%, var(--raised) 50%, var(--surface-2) 70%);
    background-size: 300% 100%;
    animation: shimmer 1.4s linear infinite;
  }
  @keyframes shimmer {
    from {
      background-position: 100% 0;
    }
    to {
      background-position: 0 0;
    }
  }
</style>
